"use client";

import { AppDownload } from "@faire/retailer-visitor-shared/components/MobileAppPromoBanner/AppDownload";
import { useAppDownloadState } from "@faire/retailer-visitor-shared/components/MobileAppPromoBanner/AppDownloadState";
import { LazyPhoneInput } from "@faire/retailer-visitor-shared/components/PhoneInput/LazyPhoneInput";
import { optimizeImageUrl } from "@faire/web/common/images/optimizeImages";
import { useStrictLocalization } from "@faire/web/common/localization/localize";
import { useIsSSR } from "@faire/web/common/server-side-rendering/isSSR";
import { Button } from "@faire/web/slate/Button";
import { Column, Flex, Row } from "@faire/web/slate/Layout";
import { LoadingSpinner } from "@faire/web/slate/LoadingSpinner";
import { getSpacing, Spacer } from "@faire/web/slate/spacing";
import { responsiveMediaQueries } from "@faire/web/slate/Theme/responsiveMediaQueries";
import { Typography } from "@faire/web/slate/Typography";
import { StrictLocalMsg } from "@faire/web/ui/lib/localization";
import { ResponsiveContentV2 } from "@faire/web/ui/ResponsiveContentV2";
import { WhenVisibilityIsImminent } from "@faire/web/ui/VisibilitySensor/WhenVisibilityIsImminent";
import React, { Suspense, useCallback, useMemo } from "react";
import styled from "styled-components";

interface IProps {
  eventPrefix?: string;
  header?: string;
  body?: string;
  onClick?: () => void;
}

export const AppDownloadPromoBanner: React.FC<IProps> = (props) => {
  const { eventPrefix, header, body, onClick } = props;
  const { strictLocalize } = useStrictLocalization();

  const message = useMemo(() => {
    return strictLocalize({
      id: "8CJi4w",
      defaultMessage:
        "Download the Faire app for a better shopping experience.",
    });
  }, [strictLocalize]);

  const isSSR = useIsSSR();

  return (
    <Wrapper>
      <BannerImage $isSSR={isSSR} />
      <Content>
        <Typography variant="pageHeaderSerifRegular">
          {header ?? (
            <StrictLocalMsg
              id="Wyugqf"
              defaultMessage="Wholesale whenever, wherever."
              description={{
                text: "Title for a promotional banner to download our app",
              }}
            />
          )}
        </Typography>
        <Spacer height="1x" />
        <Typography>{body ?? message}</Typography>
        <Spacer
          height={{
            mobileAndAbove: "2x",
            tabletAndAbove: "3x",
          }}
        />
        <ResponsiveContentV2
          tabletAndBelow={
            <AppDownload onClick={onClick} eventPrefix={eventPrefix} noModal />
          }
          desktopAndAbove={
            <DesktopAppDownloadPhoneInputForm
              onClick={onClick}
              eventPrefix={eventPrefix}
            />
          }
        />
      </Content>
    </Wrapper>
  );
};

export const DesktopAppDownloadPhoneInputForm: React.FC<
  Pick<IProps, "eventPrefix" | "onClick">
> = (props) => {
  const { eventPrefix, onClick } = props;

  const { sendAppLink, isInvalidPhoneNumber, setPhoneNumber, phoneNumber } =
    useAppDownloadState();
  const { strictLocalize } = useStrictLocalization();

  const onPhoneInputButtonClick = useCallback(() => {
    onClick?.();
    sendAppLink(eventPrefix);
  }, [sendAppLink, onClick, eventPrefix]);

  const getPhoneInputButton = (disabled?: boolean) => (
    <PhoneInputButton
      disabled={disabled || false}
      onClick={onPhoneInputButtonClick}
    >
      <StrictLocalMsg
        id="9QOwVP"
        defaultMessage="Send Download Link"
        description={{
          text: "Button to send an SMS to user mobile with our app download link",
        }}
      />
    </PhoneInputButton>
  );

  const loadingFallback = (
    <>
      <Column
        align="center"
        style={{ flexGrow: 1, alignSelf: "center", maxWidth: "300px" }}
      >
        <LoadingSpinner size="medium" />
      </Column>
      <Spacer width="2x" />
      {getPhoneInputButton(true)}
    </>
  );

  return (
    <Row align="flex-start">
      <WhenVisibilityIsImminent>
        <Suspense fallback={loadingFallback}>
          <div style={{ flexGrow: 1, maxWidth: "300px" }}>
            <LazyPhoneInput
              helper={strictLocalize({
                id: "Zp4LWZ",
                defaultMessage: "Standard messaging rates apply",
                description: {
                  text: "Warning when sending sms to an user",
                },
              })}
              error={
                isInvalidPhoneNumber
                  ? strictLocalize({
                      id: "FPcbu0",
                      defaultMessage: "Please enter a valid phone number",
                    })
                  : undefined
              }
              onChangePhoneNumber={setPhoneNumber}
              defaultPhoneNumber={phoneNumber}
            />
          </div>
          <Spacer width="2x" />
          {getPhoneInputButton()}
        </Suspense>
      </WhenVisibilityIsImminent>
    </Row>
  );
};

const Wrapper = styled(Flex).attrs({
  direction: { mobileAndAbove: "column", tabletAndAbove: "row-reverse" },
})`
  /* Need to hard code this background-color because of the banner image */
  background-color: #f4f4f0;
`;

const Content = styled(Column)`
  width: 100%;
  padding: ${getSpacing("3x")};

  ${responsiveMediaQueries.tabletAndAbove} {
    padding: ${getSpacing("5x")};
  }
`;

const PhoneInputButton = styled(Button)`
  height: 40px;
  width: fit-content;
  min-width: 160px;
  flex-shrink: 0;
`;

const getBackgroundUrl = ({ $isSSR }: { $isSSR: boolean }) =>
  optimizeImageUrl(
    {
      url: "https://cdn.faire.com/fastly/57014213f9cd3f0457e8d177e5f88506c1f3468db862b0f58f9a9344b3574078.png",
    },
    $isSSR,
    { width: 600 }
  );

const BannerImage = styled.div<{ $isSSR: boolean }>`
  flex-shrink: 0;
  width: 100%;
  height: 172px;
  background-image: url(${getBackgroundUrl});
  background-size: cover;
  background-position: center;

  ${responsiveMediaQueries.tablet} {
    background-position: left;
  }

  ${responsiveMediaQueries.tabletAndAbove} {
    width: 50%;
    height: auto;
  }
`;
